import { useLocation } from "wouter";
import { Home, Users, User, MessageCircle, BookOpen } from "lucide-react";
import UserAvatar from "./UserAvatar";
import { motion, AnimatePresence } from "framer-motion";
import { useProfile } from "@/lib/api";
import cx from "classnames";
import Logo from "@/components/Logo";
import { useEffect, useState, useRef, useLayoutEffect, useMemo } from "react";
import { useConversationsSafe, isViewingChatConversation } from "./chat";
import { ThemeToggle } from "@/components/ux/ThemeToggle";
import { ShieldUser } from "lucide-react";
import { useT } from "@/lib/i18n";
import { useCustomEvents, useClubsTab } from "@/lib/features";
// Outclose mark as inline JSX so the fill follows `currentColor` — that lets
// the parent's `text-*` classes drive active/inactive + light/dark coloring
// the same way lucide icons do (light icon on the dark active pill, dark icon
// on the white-in-dark-mode active pill). Path mirrors assets/icons/outclose.svg.
function OutcloseIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 378.5 435"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className={className}
      fill="currentColor"
    >
      <path d="M325.3,317.8h0c24.1-25.9,38.9-60.6,38.9-98.6s-13.2-68.8-34.9-94.2c0,0,0,0,0,0,2.1-1.7,18.4-25.5,20.1-27.7,11.4-15,8.6-36.4-6.4-47.8-5-3.8-10.3-7.4-15.6-10.8-33.4-20.8-71.9-31.2-111.2-30-6.3.2-12.7.7-18.9,1.5-18.7,2.4-31.9,19.4-29.5,38.1.4,3.4,4,30.5,5.3,33.4,0,0,0,0,0,0-2.8.9-5.5,2-8.2,3.1,0,0,0,0,0,0-.8-2.1-14.7-27.6-16-29.6-10.2-15.8-31.3-20.4-47.1-10.2-5.3,3.4-10.5,7.1-15.4,11-30.9,24.4-53.7,57-65.9,94.4-2,6-3.7,12.1-5,18.3-4.1,18.4,7.4,36.6,25.8,40.7,2.5.6,30.7,5.8,33.1,5.8s0,0,0,0c0,1.3,0,2.6,0,3.9s0,3.5.1,5.2c0,0,0,0,0,0-3.2-.2-30,4.3-33.3,5.1-18.4,4.2-29.9,22.4-25.7,40.8,1.4,6.2,3.1,12.3,5.1,18.3,12.4,37.4,35.3,69.9,66.2,94.2,4.9,3.9,10.2,7.6,15.5,11,5.7,3.6,12,5.4,18.3,5.4,11.2,0,22.3-5.6,28.8-15.7,1.4-2.2,12.2-28.5,13.1-30.8,0,0,0,0,0,0,3.4,1.5,6.9,2.8,10.4,3.9,0,0,0,0,0,0-.4,1.6-4.5,31.9-4.7,33.6-2.3,18.7,10.9,35.7,29.6,38.1,6.3.8,12.6,1.3,18.9,1.5,1.9,0,3.8,0,5.8,0,37.3,0,73.6-10.4,105.4-30.2,5.3-3.3,10.6-7,15.6-10.8,14.9-11.5,17.8-32.9,6.3-47.8-1.4-1.9-22.6-21.6-24.3-23.1ZM142.4,219.2c0-42.3,34.4-76.8,76.8-76.8s76.8,34.4,76.8,76.8-34.4,76.8-76.8,76.8-76.8-34.4-76.8-76.8Z" />
    </svg>
  );
}

// Define the NavItem type
type NavItem = {
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  otherPaths?: string[];
  matchPrefix?: boolean;
  badge?: number;
  tag?: string;
};

// Hook to calculate active nav indicator position (immune to scroll)
function useNavIndicator(
  navItems: NavItem[],
  location: string
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [indicatorStyle, setIndicatorStyle] = useState<{
    left: number;
    width: number;
  } | null>(null);

  useLayoutEffect(() => {
    // Clean up refs for items that no longer exist
    const currentPaths = new Set(navItems.map(item => item.path));
    for (const [path] of itemRefs.current) {
      if (!currentPaths.has(path)) {
        itemRefs.current.delete(path);
      }
    }

    const measure = () => {
      const container = containerRef.current;
      if (!container) return;

      const activeItem = navItems.find(
        (item) =>
          item.otherPaths?.some((path) => location === path) ||
          location === item.path ||
          (item.matchPrefix && location.startsWith(item.path + "/"))
      );
      const itemEl = activeItem && itemRefs.current.get(activeItem.path);
      if (!activeItem || !itemEl) {
        setIndicatorStyle(null);
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const itemRect = itemEl.getBoundingClientRect();
      // Items aren't laid out yet (e.g. before web fonts load) — skip; the
      // ResizeObserver below will re-fire once the real widths land.
      if (itemRect.width === 0) return;

      setIndicatorStyle({
        left: itemRect.left - containerRect.left,
        width: itemRect.width,
      });
    };

    measure();

    // Re-measure on layout changes. ResizeObserver fires once immediately on
    // observe (the extra layout run after the initial render) and again when
    // font loading / viewport changes resize the items — which is what was
    // leaving the indicator mis-sized or missing on first paint.
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => measure());
      if (containerRef.current) observer.observe(containerRef.current);
      for (const el of itemRefs.current.values()) observer.observe(el);
    }

    // Web fonts changing the label widths after load is the common offender.
    let cancelled = false;
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.ready.then(() => {
      if (!cancelled) measure();
    });

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [location, navItems]);

  const setItemRef = (path: string) => (el: HTMLElement | null) => {
    if (el) itemRefs.current.set(path, el);
  };

  return { containerRef, setItemRef, indicatorStyle };
}

export default function Navigation() {
  const { t } = useT();
  const [location, setLocation] = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);

  const { data: profile } = useProfile();
  const conversationsContext = useConversationsSafe();
  const totalUnreadCount = conversationsContext?.totalUnreadCount ?? 0;
  const customEventsEnabled = useCustomEvents();
  const clubsTabEnabled = useClubsTab();

  const isAdmin = profile?.data?.isAdmin ?? false;

  // Check if we're viewing a specific chat conversation (not just /chat)
  const isInChatConversation = isViewingChatConversation(location);

  // Add scrolled class when window is scrolled
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems: NavItem[] = useMemo(() => [
    { path: "/dashboard", icon: Home, label: t('common.navigation.home'), otherPaths: ["/"] },
    { path: "/connections", icon: Users, label: t('common.navigation.connections') },
    // Discover — feature-flagged entry-point to the public host-events
    // directory at /events (also covers /events/:slug and /events/new).
    // In the clubs_tab experiment, Clubs takes Discover's slot.
    ...(clubsTabEnabled
      ? [
          {
            path: "/clubs",
            icon: BookOpen,
            label: t('common.navigation.clubs'),
            matchPrefix: true,
          } satisfies NavItem,
        ]
      : customEventsEnabled
        ? [
            {
              path: "/events",
              icon: OutcloseIcon,
              label: "Discover",
              matchPrefix: true,
            } satisfies NavItem,
          ]
        : []),
    {
      path: "/chat",
      icon: MessageCircle,  
      label: t('common.navigation.chat'),
      matchPrefix: true,
      badge: totalUnreadCount,
    },
    { path: "/profile", icon: User, label: t('common.navigation.profile'), matchPrefix: true, },
  ], [t, customEventsEnabled, clubsTabEnabled, totalUnreadCount]);

  // Shared indicator logic for both desktop and mobile
  const desktopNav = useNavIndicator(navItems, location);
  const mobileNav = useNavIndicator(navItems, location);

  return (
    <>
      {/* Desktop nav - always visible */}
      <nav
        className={cx("nav-bar hidden md:block top-safe-padding", {
          "bg-white dark:bg-neutral-900": isScrolled,
        })}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Logo className="flex" />
            {/* Nav Items */}
            <div ref={desktopNav.containerRef} className="relative flex items-center space-x-2">
              {/* Desktop indicator */}
              {desktopNav.indicatorStyle && (
                <motion.div
                  className="absolute top-1/2 -translate-y-1/2 h-10 rounded-full -z-10 pointer-events-none bg-type-800 dark:bg-white"
                  initial={false}
                  animate={{
                    left: desktopNav.indicatorStyle.left,
                    width: desktopNav.indicatorStyle.width,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 380,
                    damping: 35,
                  }}
                />
              )}
              {navItems.map((item) => {
                const isActive =
                  item.otherPaths?.some((path) => location === path) ||
                  location === item.path ||
                  (item.matchPrefix && location.startsWith(item.path + "/"));
                return (
                  <button
                    key={item.path}
                    type="button"
                    ref={desktopNav.setItemRef(item.path)}
                    className="relative px-4 py-2 rounded-xl flex items-center transition-colors group isolate cursor-pointer"
                    onClick={() => { if (!isActive) setLocation(item.path); }}
                  >
                    <div
                      className={cx("relative", {
                        "text-primary-50 dark:text-black": isActive,
                        "text-type-800 dark:text-neutral-200 group-hover:text-primary-600":
                          !isActive,
                      })}
                    >
                      <item.icon className="relative w-5 h-5 inline-block" />
                      <span className="relative text-sm font-medium ml-2">
                        {item.label}
                      </span>
                    </div>

                    {!!item.badge && item.badge > 0 && (
                      <span className="absolute z-100 -bottom-1.5 -right-1.5 bg-amber-500 text-white text-[10px] font-bold rounded-full min-w-[24px] h-[24px] flex items-center justify-center px-1">
                        {item.badge > 99 ? "99+" : item.badge}
                      </span>
                    )}

                    {!!item.tag && (
                      <span className="absolute z-100 -bottom-1.5 -right-1.5 bg-amber-500 text-white text-[10px] font-bold rounded-full min-w-[24px] h-[24px] flex items-center justify-center px-1">
                        {item.tag}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* User Button */}
            <div className="flex flex-row space-x-2 items-center">
              {isAdmin && (
                <a
                  href="/admin"
                  target="_blank"
                  className="btn-primary inline-block"
                >
                  Admin
                </a>
              )}
              <ThemeToggle />
              <UserAvatar afterSignOutUrl="/" />
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile nav - hidden when viewing a chat conversation */}
      <AnimatePresence>
        {!isInChatConversation && (
          <motion.nav
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className={cx("nav-bar md:hidden ")}
          >
            <div className="max-w-7xl mx-auto px-6">
              <div ref={mobileNav.containerRef} className="relative flex items-center justify-around h-16">
                {/* Mobile indicator */}
                {mobileNav.indicatorStyle && (
                  <motion.div
                    className="absolute top-1/2 -translate-y-1/2 h-10 rounded-full -z-10 pointer-events-none bg-type-800 dark:bg-white"
                    initial={false}
                    animate={{
                      left: mobileNav.indicatorStyle.left,
                      width: mobileNav.indicatorStyle.width,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 380,
                      damping: 35,
                    }}
                  />
                )}
                {navItems.map((item) => {
                  const isActive =
                    item.otherPaths?.some((path) => location === path) ||
                    location === item.path ||
                    (item.matchPrefix && location.startsWith(item.path + "/"));
                  return (
                    <button
                      type="button"
                      ref={mobileNav.setItemRef(item.path)}
                      className="relative px-4 py-2 rounded-xl flex items-center transition-colors group isolate cursor-pointer"
                      onClick={() => { if (!isActive) setLocation(item.path); }}
                      onPointerDown={() => { if (!isActive) setLocation(item.path); }}
                    >
                      <div
                        className={cx("relative", {
                          "text-primary-50 dark:text-black": isActive,
                          "text-type-800 dark:text-neutral-200 group-hover:text-primary-800 dark:group-hover:text-primary-800":
                            !isActive,
                        })}
                      >
                        <item.icon className="relative w-5 h-5 inline-block" />
                      </div>

                      {!!item.badge && item.badge > 0 && (
                        <span className="absolute z-100 -bottom-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[24px] h-[24px] flex items-center justify-center px-1">
                          {item.badge > 99 ? "99+" : item.badge}
                        </span>
                      )}

                      {!!item.tag && (
                        <span className="absolute z-100 -top-1.5 -right-1.5 bg-amber-600 text-white text-[10px] font-bold rounded-full min-w-[24px] h-[24px] flex items-center justify-center px-1">
                          {item.tag}
                        </span>
                      )}
                    </button>
                  );
                })}
                {isAdmin && (
                  <a
                    href="/admin"
                    target="_blank"
                    className="inline-block relative px-4 py-2 rounded-xl text-type-800 dark:text-neutral-200 group-hover:text-primary-800 dark:group-hover:text-primary-800"
                  >
                    <div>
                      <ShieldUser className="relative w-5 h-5 inline-block" />
                    </div>
                  </a>
                )}
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
}
